import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';
import './Management.css';

const OrderManagement = () => {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);

  const statuses = ['pending', 'paid', 'processing', 'shipped', 'completed', 'cancelled'];

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'In afwachting',
      paid: 'Betaald',
      processing: 'Verwerken',
      shipped: 'Verzonden',
      completed: 'Voltooid',
      cancelled: 'Geannuleerd',
    };
    return labels[status] || status;
  };

  useEffect(() => {
    fetchOrders();
  }, [token]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/orders?sort=id:desc`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Orders API Error:', errorData);
        throw new Error('Kan bestellingen niet laden');
      }
      const data = await response.json();
      console.log('✅ Orders fetched:', data.data?.length || 0);
      setOrders(data.data || []);
    } catch (err) {
      console.error('❌ Error:', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (orderId) => {
    if (!confirm(`Bestelling #${orderId} verwijderen? Dit kan niet ongedaan worden gemaakt.`)) return;
    try {
      const response = await fetch(`${API_URL}/api/orders/${orderId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Verwijderen mislukt');
      await fetchOrders();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      setUpdatingStatus(orderId);
      const response = await fetch(`${API_URL}/api/orders/${orderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ data: { status: newStatus } }),
      });

      if (!response.ok) throw new Error('Failed to update order');
      await fetchOrders();
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingStatus(null);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#ffd60a',
      paid: '#00d9ff',
      processing: '#ff006e',
      shipped: '#00bcd4',
      completed: '#4caf50',
      cancelled: '#ff4444',
    };
    return colors[status] || '#aaa';
  };

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Bestellingenbeheer</h1>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <p className="loading">Bestellingen worden geladen...</p>
      ) : orders.length === 0 ? (
        <p className="empty">Geen bestellingen gevonden</p>
      ) : (
        <div className="items-table">
          <table>
            <thead>
              <tr>
                <th>Bestelling-ID</th>
                <th>Klant</th>
                <th>Totaal</th>
                <th>Status</th>
                <th>Datum</th>
                <th>Acties</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <React.Fragment key={order.id}>
                  <tr>
                    <td>#{order.id}</td>
                    <td>{order.attributes.customer_name}</td>
                    <td>€{((order.attributes.orderTotal || 0) / 100).toFixed(2)}</td>
                    <td>
                      <span
                        className="status-badge"
                        style={{ backgroundColor: getStatusColor(order.attributes.status) }}
                      >
                        {getStatusLabel(order.attributes.status)}
                      </span>
                    </td>
                    <td>{new Date(order.attributes.createdAt).toLocaleDateString('nl-NL')}</td>
                    <td className="actions">
                      <button
                        className="btn-view"
                        onClick={() =>
                          setSelectedOrder(
                            selectedOrder?.id === order.id ? null : order
                          )
                        }
                      >
                        {selectedOrder?.id === order.id ? 'Verbergen' : 'Bekijken'}
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(order.id)}
                      >
                        Verwijderen
                      </button>
                    </td>
                  </tr>
                  {selectedOrder?.id === order.id && (
                    <tr className="detail-row">
                      <td colSpan="6">
                        <div className="order-detail">
                          <div className="detail-section">
                            <h4>Klantgegevens</h4>
                            <p>
                              <strong>Naam:</strong> {order.attributes.customer_name}
                            </p>
                            <p>
                              <strong>E-mail:</strong> {order.attributes.customer_email}
                            </p>
                          </div>

                          <div className="detail-section">
                            <h4>Bestelde Artikelen</h4>
                            <p>
                              {(() => {
                                try {
                                  const items = JSON.parse(order.attributes.items);
                                  return Array.isArray(items)
                                    ? items.map((item, idx) => (
                                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                                          {item.customization?.imageUrl && (
                                            <a
                                              href={`${API_URL}${item.customization.imageUrl}`}
                                              download
                                              target="_blank"
                                              rel="noreferrer"
                                              title="Download ontwerp"
                                            >
                                              <img
                                                src={`${API_URL}${item.customization.imageUrl}`}
                                                alt="Sticker ontwerp"
                                                style={{ width: 48, height: 48, objectFit: 'contain', filter: 'drop-shadow(0 0 0 3px #fff)', background: 'transparent', flexShrink: 0, cursor: 'pointer' }}
                                              />
                                            </a>
                                          )}
                                          <span>
                                            {item.name} x {item.quantity || 1}
                                            {item.customization && (
                                              <span style={{ marginLeft: '0.5rem', color: '#00d9ff', fontSize: '0.8rem' }}>
                                                Die-cut {item.customization.size} ({item.customization.dimensions})
                                              </span>
                                            )}
                                          </span>
                                        </div>
                                      ))
                                    : order.attributes.items;
                                } catch {
                                  return order.items;
                                }
                              })()}
                            </p>
                          </div>

                          <div className="detail-section">
                            <h4>Status Bijwerken</h4>
                            <div className="status-selector">
                              {statuses.map((status) => (
                                <button
                                  key={status}
                                  className={`status-btn ${order.attributes.status === status ? 'active' : ''}`}
                                  onClick={() => handleStatusUpdate(order.id, status)}
                                  disabled={updatingStatus === order.id}
                                  style={{
                                    backgroundColor:
                                      order.attributes.status === status
                                        ? getStatusColor(status)
                                        : 'transparent',
                                  }}
                                >
                                  {getStatusLabel(status)}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default OrderManagement;
